import Vue from 'vue';
import { fetchAuthSession } from 'aws-amplify/auth';

// Live-incident notifications over a raw WebSocket (separate channel from the
// AppSync `notifications` module). Payload shape: { type: 'live_incident',
// payload: { action, eventId, incidentId, groupId, severity, anomalies[], ... } }.

const WS_URL = 'wss://htz-de-svc01.hosts.elas.tech:9223';

// --- Auth -------------------------------------------------------------------
// Browsers CANNOT set the `Authorization` header on a WebSocket, and we keep the
// token out of the URL (it would leak into proxy/access logs). Instead the
// backend authenticates on the FIRST frame after open:
//   { type: 'auth', payload: { token: 'Bearer <jwt>' } }
// The socket is unauthenticated until that frame is accepted. If the backend
// changes the handshake, the ws.onopen handler is the single place to change.

// Async report/AI-analysis progress events share this socket. Older/backend
// direct frames carry { eventType, reportId, eventId, ... }; notification-bus
// frames carry { type: 'report_analysis_update', payload: { eventType, ... } }.
// Both are routed into the eventAnalytics module rather than the incident list.
const REPORT_EVENT_TYPES = new Set([
  'report_generation_started',
  'report_generation_completed',
  'report_generation_failed',
  'ai_analysis_started',
  'ai_analysis_incident_completed',
  'ai_analysis_event_summary_completed',
  'ai_analysis_completed',
  'ai_analysis_failed',
]);

const BASE_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 30000;
const MAX_INCIDENTS = 100;

// Non-reactive connection handles live at module scope (mirrors how
// store/Notifications.js keeps its subscription registry outside Vuex state).
let socket = null;
let reconnectTimer = null;
let reconnectAttempts = 0;
let manualClose = false;

const normSeverity = (value) => {
  const key = `${value || ''}`.trim().toLowerCase();
  if (key === 'error' || key === 'critical' || key === 'failed') return 'error';
  if (key === 'warning' || key === 'warn') return 'warning';
  return 'info';
};

async function getToken() {
  try {
    const session = await fetchAuthSession();
    // Access token authorizes calls to the backend (resource server); the id
    // token only identifies the user to the client. Backend WS auth wants access.
    return (session.tokens && session.tokens.accessToken && session.tokens.accessToken.toString())
      || (session.tokens && session.tokens.idToken && session.tokens.idToken.toString())
      || null;
  } catch (error) {
    return null;
  }
}

// First-frame auth message. Token carries the `Bearer ` prefix for parity with
// the HTTP Authorization scheme; the backend accepts it with or without.
function buildAuthMessage(token) {
  return JSON.stringify({ type: 'auth', payload: { token: `Bearer ${token}` } });
}

// Flatten a live_incident payload into a notification record. Distinct
// resources (usually a redundant A/B pair) are collected for display + deep-link.
function toIncident(payload) {
  const anomalies = Array.isArray(payload.anomalies) ? payload.anomalies : [];
  const resources = [];
  const seen = new Set();
  anomalies.forEach((a) => {
    if (!a || typeof a !== 'object') return;
    const key = a.resourceId || a.resourceName;
    if (!key || seen.has(key)) return;
    seen.add(key);
    resources.push({
      id: a.resourceId || '',
      name: a.resourceName || a.resourceId || '',
      type: `${a.resourceType || ''}`.toUpperCase(),
    });
  });
  return {
    id: payload.incidentId || `${payload.eventId || 'evt'}-${payload.startTime || Date.now()}`,
    action: payload.action || 'updated',
    eventId: payload.eventId || '',
    monitoredEventId: payload.monitoredEventId || '',
    severity: normSeverity(payload.severity),
    groupId: payload.groupId || '',
    groupName: payload.groupName || '',
    phase: payload.phase || '',
    startTime: payload.startTime || null,
    endTime: payload.endTime || null,
    durationSeconds: Number(payload.durationSeconds) || 0,
    anomalyCount: Number(payload.anomalyCount) || anomalies.length,
    resources,
    receivedAt: Date.now(),
    read: false,
    raw: payload,
  };
}

const state = {
  connected: false,
  connecting: false,
  incidents: [], // newest first
  lastError: null,
  // Bumped on every incoming incident so the UI can fire a toast.
  lastSignalSeq: 0,
  lastSignal: null,
};

const getters = {
  liveIncidents: (s) => s.incidents,
  liveUnreadCount: (s) => s.incidents.filter((i) => !i.read).length,
  liveConnected: (s) => s.connected,
};

const mutations = {
  setConnected: (s, value) => {
    s.connected = value;
    if (value) s.connecting = false;
  },
  setConnecting: (s, value) => { s.connecting = value; },
  setError: (s, error) => { s.lastError = error || null; },
  upsertIncident: (s, incident) => {
    const idx = s.incidents.findIndex((i) => i.id === incident.id);
    if (idx === -1) {
      s.incidents.unshift(incident);
      if (s.incidents.length > MAX_INCIDENTS) s.incidents.splice(MAX_INCIDENTS);
    } else {
      // Keep position, refresh fields, mark unread again (new info arrived).
      Vue.set(s.incidents, idx, { ...s.incidents[idx], ...incident, read: false });
    }
    s.lastSignal = incident;
    s.lastSignalSeq += 1;
  },
  markRead: (s, id) => {
    const idx = s.incidents.findIndex((i) => i.id === id);
    if (idx !== -1) Vue.set(s.incidents, idx, { ...s.incidents[idx], read: true });
  },
  markAllRead: (s) => { s.incidents = s.incidents.map((i) => ({ ...i, read: true })); },
  clearAll: (s) => { s.incidents = []; },
};

const actions = {
  async connect({ commit, dispatch }) {
    if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) {
      return;
    }
    manualClose = false;
    commit('setConnecting', true);

    const token = await getToken();
    let ws;
    try {
      ws = new WebSocket(WS_URL);
    } catch (error) {
      commit('setConnecting', false);
      commit('setError', error.message || 'WebSocket open failed');
      dispatch('scheduleReconnect');
      return;
    }
    socket = ws;

    ws.onopen = () => {
      reconnectAttempts = 0;
      commit('setConnected', true);
      commit('setError', null);
      // Authenticate on the first frame — the backend rejects everything until
      // it accepts this. No token means no auth, so let the close handler back off.
      if (token) {
        try { ws.send(buildAuthMessage(token)); } catch (error) { /* onclose handles it */ }
      } else {
        commit('setError', 'No auth token for WebSocket');
      }
    };
    ws.onmessage = (event) => dispatch('handleMessage', event.data);
    ws.onerror = () => { commit('setError', 'WebSocket error'); };
    ws.onclose = (event) => {
      commit('setConnected', false);
      // Clear `connecting` so the UI doesn't sit on "Connecting…" forever when
      // the handshake fails (it shows "Disconnected" during the backoff wait).
      commit('setConnecting', false);
      if (event && (event.code || event.reason)) {
        commit('setError', `closed ${event.code}${event.reason ? `: ${event.reason}` : ''}`);
        // Close code/reason tells auth (e.g. 4401/1008) vs network (1006) apart.
        // eslint-disable-next-line no-console
        console.warn('[liveNotifications] WebSocket closed', event.code, event.reason);
      }
      if (socket === ws) socket = null;
      if (!manualClose) dispatch('scheduleReconnect');
    };
  },

  scheduleReconnect({ dispatch }) {
    if (manualClose || reconnectTimer) return;
    const delay = Math.min(BASE_BACKOFF_MS * (2 ** reconnectAttempts), MAX_BACKOFF_MS);
    reconnectAttempts += 1;
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      dispatch('connect');
    }, delay);
  },

  disconnect({ commit }) {
    manualClose = true;
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    reconnectAttempts = 0;
    if (socket) {
      try { socket.close(); } catch (error) { /* noop */ }
      socket = null;
    }
    commit('setConnected', false);
    commit('setConnecting', false);
  },

  handleMessage({ commit, dispatch }, data) {
    let msg;
    try {
      msg = typeof data === 'string' ? JSON.parse(data) : data;
    } catch (error) {
      return;
    }
    if (!msg || typeof msg !== 'object') return;
    if (msg.type === 'live_incident' && msg.payload) {
      commit('upsertIncident', toIncident(msg.payload));
      return;
    }
    const reportMessage = msg.type === 'report_analysis_update' && msg.payload
      ? msg.payload
      : msg;
    const isReportEvent = typeof reportMessage.eventType === 'string'
      && REPORT_EVENT_TYPES.has(reportMessage.eventType);
    if (isReportEvent) {
      dispatch(
        'eventAnalytics/applyReportNotification',
        reportMessage,
        { root: true },
      );
    }
  },

  markRead({ commit }, id) { commit('markRead', id); },
  markAllRead({ commit }) { commit('markAllRead'); },
  clearAll({ commit }) { commit('clearAll'); },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
